import React, { useEffect, useMemo, useRef, useState } from "react";
import usmApi, { USM_BASE, USM_TOKEN_KEY } from "@/lib/usmApi";
import { toast } from "sonner";
import { Printer, Download, Copy } from "lucide-react";

/**
 * Argox/BarTender-friendly label print dialog.
 *
 * Workflow:
 *   1. Renders a live PNG preview using the active label profile.
 *   2. Quantity picker (1–50) — opens print window with N stacked copies.
 *   3. Print → browser native print dialog (silent on Argox driver).
 *   4. Download → saves PNG so it can be loaded into BarTender or USB-printed.
 */
export default function LabelPrintDialog({ item, category, onClose }) {
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState("");
  const [qty, setQty] = useState(1);
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const lastBlobRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await usmApi.get("/labels/profiles");
        if (!mounted) return;
        const list = r.data || [];
        setProfiles(list);
        const def = list.find((p) => p.is_default) || list[0];
        setProfileId(def?.id || "");
      } catch (e) {
        toast.error("Etiket profilleri yüklenemedi");
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Re-render preview when profile changes
  useEffect(() => {
    if (!profileId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const token = localStorage.getItem(USM_TOKEN_KEY);
        const r = await fetch(`${USM_BASE}/inventory/${item.id}/label.png?profile=${encodeURIComponent(profileId)}&t=${Date.now()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error("Önizleme oluşturulamadı");
        const blob = await r.blob();
        if (cancelled) return;
        if (lastBlobRef.current) URL.revokeObjectURL(lastBlobRef.current);
        const url = URL.createObjectURL(blob);
        lastBlobRef.current = url;
        setPreviewUrl(url);
      } catch (e) { toast.error(e.message || "Önizleme hatası"); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [profileId, item.id]);

  // Clean up blob url on unmount
  useEffect(() => () => { if (lastBlobRef.current) URL.revokeObjectURL(lastBlobRef.current); }, []);

  const profile = useMemo(() => profiles.find((p) => p.id === profileId), [profiles, profileId]);

  const handlePrint = () => {
    if (!previewUrl) return;
    const win = window.open("", "_blank", "width=600,height=500");
    if (!win) { toast.error("Yazdırma penceresi açılamadı. Pop-up'a izin verin."); return; }
    const copies = Array.from({ length: Math.max(1, Math.min(50, qty)) })
      .map(() => `<div class="lbl"><img src="${previewUrl}" /></div>`).join("");
    win.document.write(`<!doctype html>
<html><head><title>Etiket ${item.stock_code || item.barcode}</title>
<style>
  @page { size: ${profile?.width_mm || 50}mm ${profile?.height_mm || 25}mm; margin: 0; }
  html, body { margin: 0; padding: 0; background: #fff; }
  .lbl { width: ${profile?.width_mm || 50}mm; height: ${profile?.height_mm || 25}mm; page-break-after: always; display: flex; align-items: center; justify-content: center; overflow: hidden; }
  .lbl:last-child { page-break-after: auto; }
  /* CRITICAL FOR BARCODE SCAN RELIABILITY:
   *  image-rendering: pixelated  → tells the browser to use nearest-neighbor
   *  when rasterising the source PNG to printer dots. The default (bilinear)
   *  interpolation creates grey/intermediate pixels along bar edges, which
   *  thermal printers render as inconsistent dot patterns — barcodes then
   *  read with mis-counted modules and scanners reject them.
   *  We size the image in mm (matching @page) so the PNG's 300-dpi intrinsic
   *  dimensions land at exactly the requested physical size. */
  img {
    width: ${profile?.width_mm || 50}mm;
    height: ${profile?.height_mm || 25}mm;
    object-fit: fill;
    display: block;
    image-rendering: pixelated;
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }
</style></head>
<body>${copies}
<script>
  window.addEventListener('load', function(){ setTimeout(function(){ window.print(); }, 150); });
  window.addEventListener('afterprint', function(){ window.close(); });
</script>
</body></html>`);
    win.document.close();
  };

  const handlePrintPdf = async () => {
    try {
      const token = localStorage.getItem(USM_TOKEN_KEY);
      const url = `${USM_BASE}/inventory/${item.id}/label.pdf?profile=${encodeURIComponent(profileId)}&copies=${Math.max(1, Math.min(200, qty))}&download=false`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("PDF oluşturulamadı");
      const blob = await r.blob();
      const pdfUrl = URL.createObjectURL(blob);
      // Open the PDF in a new tab so the user can send it to the Argox driver
      // via Adobe Reader / browser PDF viewer (no Chromium image downscale).
      const win = window.open(pdfUrl, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = pdfUrl;
        a.download = `${item.stock_code || item.barcode}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast.info("Pop-up engellendi — PDF indirildi");
      } else {
        toast.success("PDF açıldı — Argox yazıcıya gönderin");
      }
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (e) {
      toast.error(e.message || "PDF yazdırma başarısız");
    }
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem(USM_TOKEN_KEY);
      const r = await fetch(`${USM_BASE}/inventory/${item.id}/label.png?profile=${encodeURIComponent(profileId)}&download=true`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("İndirme başarısız");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${item.stock_code || item.barcode}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Etiket indirildi");
    } catch (e) { toast.error(e.message || "İndirme başarısız"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="label-print-dialog" className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl">Etiket Yazdır</h2>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#71717A] mt-0.5 font-mono">
              {item.stock_code || item.barcode}
            </p>
          </div>
          <button onClick={onClose} className="text-[#71717A] text-lg leading-none">✕</button>
        </div>

        <div className="p-6 grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-6">
          {/* Preview */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#71717A] mb-2">Önizleme</div>
            <div className="bg-[#FAFAF9] border border-[#E7E5E4] p-6 flex items-center justify-center min-h-[180px]">
              {loading ? (
                <span className="text-[11px] text-[#71717A]">Yükleniyor...</span>
              ) : previewUrl ? (
                <img data-testid="label-preview-img" src={previewUrl} alt="Etiket"
                  style={{
                    width: `${(profile?.width_mm || 50) * 3.78}px`,
                    height: `${(profile?.height_mm || 25) * 3.78}px`,
                    objectFit: "contain",
                    background: "white",
                    imageRendering: "pixelated",
                  }}
                  className="shadow-sm" />
              ) : (
                <span className="text-[11px] text-[#71717A]">—</span>
              )}
            </div>
            {profile && (
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#A8A29E] mt-2">
                {profile.width_mm}×{profile.height_mm} mm · {profile.barcode_type}
              </p>
            )}
          </div>

          {/* Controls */}
          <div className="space-y-4">
            <Field label="Etiket Profili">
              <select data-testid="label-profile-select" value={profileId} onChange={(e) => setProfileId(e.target.value)} className={inputCls}>
                {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Kopya Adedi">
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2 border border-[#E7E5E4]">−</button>
                <input data-testid="label-qty-input" type="number" min="1" max="50" value={qty}
                  onChange={(e) => setQty(Math.max(1, Math.min(50, parseInt(e.target.value || "1", 10))))}
                  className={inputCls + " text-center"} />
                <button type="button" onClick={() => setQty((q) => Math.min(50, q + 1))} className="px-3 py-2 border border-[#E7E5E4]">+</button>
              </div>
            </Field>
            <div className="bg-[#FAFAF9] border border-[#F5F5F4] p-3 text-[11px] text-[#71717A] leading-relaxed">
              <p className="font-semibold text-[#0A0A0A] mb-1">Argox OS-216 için PDF Yazdır önerilir</p>
              <p>PDF, tarayıcı yeniden boyutlandırmasını atlayarak Argox'a doğrudan kayıpsız bitmap gönderir. Tarayıcı yazdırma yedek seçenek olarak bırakıldı.</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#E7E5E4] flex flex-wrap justify-end gap-3">
          <button onClick={handleDownload} data-testid="label-download-btn" className="text-xs uppercase tracking-[0.2em] px-5 py-3 border border-[#E7E5E4] hover:bg-[#FAFAF9] inline-flex items-center gap-2">
            <Download size={14} /> PNG İndir
          </button>
          <button data-testid="label-print-btn" onClick={handlePrint} disabled={!previewUrl}
            className="text-xs uppercase tracking-[0.2em] px-5 py-3 border border-[#E7E5E4] hover:bg-[#FAFAF9] disabled:opacity-40 inline-flex items-center gap-2">
            <Printer size={14} /> Tarayıcı ile Yazdır
          </button>
          <button data-testid="label-print-pdf-btn" onClick={handlePrintPdf} disabled={!profileId}
            className="bg-[#0A0A0A] disabled:opacity-40 text-white text-xs uppercase tracking-[0.2em] px-6 py-3 inline-flex items-center gap-2"
            title="Argox için önerilen — PDF olarak açar, tarayıcı yeniden boyutlandırması yapmaz">
            <Printer size={14} /> PDF Yazdır ({qty}) · Argox
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.15em] text-[#71717A] block mb-1.5">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 border border-[#E7E5E4] focus:border-[#C8A96A] outline-none text-sm";
